// WO-GF-001-P1B-S31 (F185) — THE ONE CONFIG VALUE FOR THE BASEMAP.
//
// FOUNDER RULING 2026-07-26, KEYLESS: "All I want is a basic map so that everything can be seen where it
// is located. It doesn't need roads." The named stack is MapLibre GL JS (BSD) + OpenFreeMap
// (MIT, no key, no signup, no cap, commercial use OK).
//
// WHY THIS FILE EXISTS AT ALL — the WO is explicit that the style URL sits behind ONE value.
// OpenFreeMap is donation-funded and publishes no SLA, so the realistic failure is not "it breaks" but
// "it gets slow, or it goes away". When that happens the fix must be a ONE-LINE repoint to a paid host
// (MapTiler, Protomaps, a self-hosted tileserver) or back to Google — not a hunt through components.
// Every map surface imports MAP_STYLE_URL from here and nothing else knows a tile host exists.
//
// WHY NOT RAW OSM TILES — banked research KB-004-KEYLESS-BASEMAP-TILE-POLICY-V1-INT. The OSM Foundation's
// tile usage policy states that access to tile.openstreetmap.org may be withdrawn at any time and
// directs production/commercial users to a paid provider or to self-hosting. "Just point at OSM" is the
// trap in this problem, not the answer, and it is excluded by ruling — do not substitute it here.

/**
 * The basemap style. Override with NEXT_PUBLIC_MAP_STYLE_URL to repoint at any MapLibre-compatible
 * style (paid host, self-hosted, or a Google-backed vector style) without touching a component.
 */
// S33 · F193 — `liberty` → `bright`. THE STYLE EVALUATION, AND ITS HONEST LIMIT.
//
// The founder wants the map to answer: where is the airport, how do I get there, what is the road, what
// cities are near. All three OpenFreeMap styles were fetched and their layer definitions compared at the
// zoom this map ACTUALLY renders at (the 45-point continental fit measures z≈2.6):
//
//   layer first appears at | liberty | bright | positron
//   city labels            |   z3    |   z3   |   z3
//   town labels            |   z6    |   z6   |   z6
//   AIRPORTS (aeroway)     |  z10    |  *z4*  |  z10
//   road lines             |   z5    |  *z4*  |   z6
//
// TWO CONCLUSIONS, and the first one matters more than the choice of style:
//
// 1. ⚠ NO KEYLESS STYLE ANSWERS THOSE QUESTIONS AT THE FITTED ZOOM, and it is not a styling problem —
//    the vector tiles carry no city, road or airport geometry below z3/z4 at all. At z2.6 every style
//    renders country labels and coastlines and nothing else. Restyling cannot conjure data the tiles do
//    not ship. THE LEVER IS ZOOM, NOT STYLE. Deliberately NOT changed here: the framing is `fitBounds`
//    over all 45 served points, and tightening it to reach z3+ would drop points off the map — that is a
//    product decision about what the map is FOR, so it is routed to the design study the WO already
//    carves out (the selector/globe study) rather than decided in a config file.
//
// 2. GIVEN THAT, `bright` IS STRICTLY THE BETTER DEFAULT. It is the same vendor, the same MIT/keyless
//    terms, and no code change beyond this string — and it shows AIRPORTS FROM z4 WHERE LIBERTY WAITS
//    UNTIL z10, plus road lines one zoom level earlier. The airport is the thing the founder named first,
//    and the moment a visitor zooms toward a region bright answers him six zoom levels sooner.
//    There is no cost at the continental fit, where the two are identical.
//
// KB-004 still governs: one config value, no new vendor, no keys. Swapping hosts remains a one-line edit.
export const MAP_STYLE_URL =
  process.env.NEXT_PUBLIC_MAP_STYLE_URL ?? "https://tiles.openfreemap.org/styles/bright";

// ─────────────────────────────────────────────────────────────────────────────
// S35 · F202 — WHY THE BASEMAP WAS BLANK, AND THE ONE VALUE THAT FIXES IT.
//
// THE ROOT CAUSE, measured rather than reasoned (evidence in the S35 close-out):
//   • the style JSON (200), its TileJSON `/planet` (200) and the sprite (200/200) all fetch fine, so
//     every "is the vendor up / is the URL right / is it a key problem" theory is excluded — and KB-004
//     finding 7 says to check the FRONTEND before blaming the provider, which is what found this;
//   • NOT ONE map tile is ever requested, on any host. `.pbf` requests: 0. `natural_earth` raster
//     requests: 0. Forcing a viewport resize produces 0 more;
//   • the map panel measures 432x693 with a live WebGL 2.0 context and paints 97.3% pure white;
//   • `new Worker(<url>, {type:"module"})` is constructed ONCE and immediately fires `error`.
//
// maplibre-gl v6 resolves its worker as `new URL("./maplibre-gl-worker.mjs", import.meta.url).href`
// from INSIDE its own bundled chunk. Under Next's dev bundler that base is a chunk URL, so the resolved
// module-worker URL is not a path the app serves; the worker dies on load. maplibre only falls back to a
// classic worker when the CONSTRUCTOR THROWS — a worker that constructs and then fails to load gets no
// fallback and emits no map `error`. Every tile load is handed to a dead actor and simply never returns,
// which is why this failed SILENTLY: no console error, no failed request, no red anything. `load` never
// fires either — the note in KeylessMap.tsx that recorded "load never fires while the basemap is visibly
// painted" had the first half right and the second half wrong; it was never painted.
//
// THE FIX IS maplibre's OWN documented remedy, not a workaround: `setWorkerUrl()` (exported by
// maplibre-gl v6 alongside `getWorkerUrl()`) points the worker at a URL WE serve. The worker file is
// vendored into `apps/root/public/` so the app owns the URL, and `mountsel/basemap-actually-paints`
// hashes that copy against the installed `maplibre-gl/dist/maplibre-gl-worker.mjs` on every run — so a
// version bump that moves the worker cannot leave a stale copy serving silently. Same shape as the style
// URL above and same KB-004 rail: ONE config value, no new vendor, no key.
/** Path (on our own origin) to the vendored maplibre worker. Kept in sync by the F202 drift gate. */
export const MAP_WORKER_URL = process.env.NEXT_PUBLIC_MAP_WORKER_URL ?? "/maplibre-gl-worker.mjs";

/** North-America framing used when a surface has no markers to fit to. Real coordinates, not a guess. */
export const MAP_DEFAULT_CENTER = { lng: -110, lat: 50 };
export const MAP_DEFAULT_ZOOM = 2.6;

/**
 * ── THE AT-REST CORRIDOR VIEW (FOUNDER MAP RULING, 2026-08-27) ────────────────────────────────────
 *
 * The ruling, verbatim in substance: the schedule-index page must mount a functional basemap
 * IMMEDIATELY, showing "a neutral Vancouver–Whistler corridor view" with "no schedule-specific stops,
 * route lines or fabricated markers"; bounds are to come from an existing authoritative place/stop
 * registry, and "if none exists, use the approved map provider's regional view without markers."
 *
 * NO SUCH REGISTRY EXISTS IN THIS TREE, and that was checked rather than assumed. Searched
 * 2026-08-27: `place-names.ts` is authored names bound to engine stop CODES and carries no lat/lng;
 * `airport-places.ts` carries none; the only coordinate sources are `transport-stops.stub.ts` — which
 * this ruling forbids on any visitor-facing surface — and `/api/transport/geo`, which reads the
 * legacy RDS and does not answer outside the VPC. The engine's own `stopDetails[].location` is
 * authoritative but is returned per ROUTE and DATE, and the schedule index has neither.
 *
 * So this is the ruling's second branch: the approved provider's regional view, with NO MARKERS. The
 * two numbers below are a VIEWPORT, not data. They assert nothing about where any stop is, nothing
 * about a route, and nothing a visitor could mistake for operational fact — they only frame the
 * corridor the page is about, the way a printed map's crop is not a claim. Nothing is drawn on it.
 *
 * The centre sits between Vancouver and Whistler on the Sea-to-Sky corridor and the zoom holds both
 * ends plus Squamish between them. THE MOMENT A ROUTE AND DATE EXIST, this is not used: the results
 * screen fits the map to the engine's returned `stopDetails[].location` instead (see BookingResults).
 */
export const MAP_CORRIDOR_VIEW = { lng: -123.0, lat: 49.65, zoom: 8.2 };

/**
 * FOUNDER-WALK-QA-STAGING-007 · A7 — THE VENDOR STYLE'S ONE RUNTIME ERROR, FIXED AT THE SOURCE.
 *
 * Measured on every page that draws a map, both viewports, as a console ERROR:
 *     layers[boundary_3].filter[1]: Expected value to be of type number, but found null instead.
 * The bright style filters its boundary layers on `[">=", ["get", "admin_level"], 3]`. Some boundary
 * features in the vendor's tiles carry NO admin_level, `>=` on null is a runtime type error in the
 * expression engine, and maplibre logs it — from the tile worker, which is why neither `validateStyle`
 * nor a map error listener could reach it. The style is not wrong about what it wants (a missing
 * level is not between 3 and 6); it is wrong about how to say it. `coalesce` says it: a missing level
 * reads as 0, fails the range, and the feature is skipped — exactly what the error path did, minus
 * the error. Scoped to `admin_level` in `boundary*` layers only; every other layer is untouched.
 *
 * Applied through maplibre's own `transformStyle` hook (setStyle), so the style URL stays the one
 * value above and the fix travels with the loaded JSON rather than a vendored copy of it.
 */
type StyleLayer = { id?: string; filter?: unknown; [k: string]: unknown };
type StyleLike = { layers?: StyleLayer[]; [k: string]: unknown };
function coalesceAdminLevel(expr: unknown): unknown {
  if (!Array.isArray(expr)) return expr;
  if (expr.length === 2 && expr[0] === "get" && expr[1] === "admin_level") return ["coalesce", expr, 0];
  return expr.map(coalesceAdminLevel);
}
export function sanitizeVendorStyle(_previous: unknown, next: StyleLike): StyleLike {
  if (!next || !Array.isArray(next.layers)) return next;
  return {
    ...next,
    layers: next.layers.map((l) =>
      l && typeof l.id === "string" && l.id.startsWith("boundary") && l.filter !== undefined
        ? { ...l, filter: coalesceAdminLevel(l.filter) }
        : l,
    ),
  };
}
